"use client"

import type React from "react"

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import { Button } from "@/components/ui/button"
import {
  Heart,
  Home,
  Palette,
  Ruler,
  Calendar,
  Eye,
  Zap,
  Wallet as Wall,
  Coffee,
  Armchair,
  Utensils,
  DoorOpen,
  Users,
  Wine,
  BedSingle,
  Flower as Shower,
  Baby,
  Gamepad2,
  UtensilsCrossed,
  ChefHat,
  Shirt,
  Sofa,
  Bath,
  BedDouble,
} from "lucide-react"
import Image from "next/image"
import type { UIGalleryItem } from "./GalleryPage"
import { ROOM_TYPE_MAPPINGS } from "@/src/utils/api-value-mappings"
import { useDisplayRoomType, useDisplayRoomTheme, useDisplayRoomDimension } from "@/hooks/useRoomMappings"

interface GalleryModalProps {
  item: UIGalleryItem | null
  isOpen: boolean
  onClose: () => void
  onLikeToggle: (itemId: string) => void
  showLoginTooltipForImage: string | null // Changed from boolean to track specific image ID
}

export function GalleryModal({ item, isOpen, onClose, onLikeToggle, showLoginTooltipForImage }: GalleryModalProps) {
  const displayRoomType = useDisplayRoomType(item?.roomType || "")
  const displayRoomTheme = useDisplayRoomTheme(item?.roomTheme || "")
  const displayRoomDimension = useDisplayRoomDimension(item?.roomSize || "")

  if (!item) return null

  const handleLike = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    console.log("[v0] Like button clicked for item:", item.id, "Current liked state:", item.isLiked)
    onLikeToggle(item.id)
  }

  const getRoomTypeIcon = (roomType: string) => {
    const mapping = ROOM_TYPE_MAPPINGS.find((r) => r.value === roomType)
    const iconName = mapping?.icon || "Home"

    const iconMap: Record<string, React.ComponentType<any>> = {
      Coffee,
      Armchair,
      Utensils,
      DoorOpen,
      Users,
      Wine,
      BedSingle,
      Shower,
      Baby,
      Gamepad2,
      UtensilsCrossed,
      ChefHat,
      Shirt,
      Sofa,
      Bath,
      BedDouble,
      Heart,
      Home,
    }

    return iconMap[iconName] || Home
  }

  const getSizeDisplayName = (size: string) => {
    return size.charAt(0).toUpperCase() + size.slice(1)
  }

  const getMoodDisplayName = (mood: string) => {
    return mood.charAt(0).toUpperCase() + mood.slice(1)
  }

  const getIntensityDisplayName = (intensity: string) => {
    return intensity.charAt(0).toUpperCase() + intensity.slice(1)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  const getColorHex = (colorName: string): string => {
    const colorMap: Record<string, string> = {
      // Neutrals
      "warm whites": "#F4EFE6",
      "warm white": "#F4EFE6",
      "cool whites": "#F8F9FA",
      "cool white": "#F8F9FA",
      griege: "#D4CFC0",
      cream: "#FDF6E3",
      "soft gray": "#E5E7EB",
      "medium gray": "#9CA3AF",
      "dark gray": "#4B5563",
      black: "#1F2937",
      mushroom: "#C7B299",
      linen: "#FAF0E6",

      // Reds
      crimson: "#DC143C",
      burgundy: "#800020",
      "blush pink": "#F4C2C2",

      // Red-Oranges
      coral: "#FF7F50",
      terracotta: "#C06E52",

      // Oranges
      "rust orange": "#B7410E",
      peach: "#FFCBA4",

      // Yellow-Oranges
      amber: "#FFBF00",

      // Yellows
      "golden yellow": "#FFD700",

      // Yellow-Greens
      chartreuse: "#7FFF00",

      // Greens
      "sage green": "#9CAF88",
      "forest green": "#355E3B",
      olive: "#808000",
      mint: "#98FB98",

      // Blue-Greens
      teal: "#008080",
      turquoise: "#40E0D0",

      // Blues
      "powder blue": "#B6D7FF",
      "navy blue": "#1B2951",
      "royal blue": "#4169E1",

      // Blue-Purples
      indigo: "#4B0082",

      // Purples
      lavender: "#C8A2C8",
      eggplant: "#614051",

      // Red-Purples
      magenta: "#FF00FF",

      // Legacy colors for backward compatibility
      red: "#ef4444",
      blue: "#3b82f6",
      green: "#22c55e",
      yellow: "#eab308",
      purple: "#a855f7",
      pink: "#ec4899",
      orange: "#f97316",
      gray: "#6b7280",
      grey: "#6b7280",
      white: "#ffffff",
      brown: "#a3a3a3",
      oak: "#d2b48c",
      beige: "#f5f5dc",
      navy: "#1e3a8a",

      // Additional common color variations
      steel: "#71717a",
      charcoal: "#374151",
      brick: "#dc2626",
      sand: "#fbbf24",
      "pastel-yellow": "#fef3c7",
      "pastel-green": "#d1fae5",
      eggplant: "#614051",
    }

    // Try exact match first, then lowercase match
    return colorMap[colorName] || colorMap[colorName.toLowerCase()] || "#9ca3af"
  }

  // Parse colors from arrays or fallback to mock data
  const preferredColors = item.preferredColors || ["Gray", "White", "Oak"]
  const avoidedColors = item.avoidColors || []

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
      <DialogContent className="max-w-7xl max-h-[95vh] w-full overflow-hidden p-0 bg-gradient-to-br from-gray-50 to-white">
        <VisuallyHidden>
          <DialogTitle>{item.title}</DialogTitle>
        </VisuallyHidden>

        <div className="flex flex-col md:flex-row h-full max-h-[95vh]">
          <div className="relative flex-1 md:flex-[2.5] min-h-[300px] md:min-h-[500px] max-h-[50vh] md:max-h-[80vh]">
            {/* Like button */}
            <div className="absolute top-4 left-4 z-10">
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="bg-white/95 hover:bg-white shadow-lg rounded-full h-10 w-10 p-0"
                  onClick={handleLike}
                >
                  <Heart className={`h-5 w-5 ${item.isLiked ? "fill-red-500 text-red-500" : ""}`} />
                </Button>
                {showLoginTooltipForImage === item.id && ( // Only show tooltip if this image's ID matches
                  <div className="absolute top-full left-0 mt-2 w-48 bg-gray-900 text-white text-xs rounded-lg p-2 shadow-lg z-20 animate-in fade-in slide-in-from-top-2 duration-200">
                    Please log in to add your like to this image.
                    <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 rotate-45"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Main image */}
            <div className="relative h-full w-full">
              <Image
                src={item.imageUrl || "/placeholder.svg"}
                alt={item.title}
                fill
                className="object-contain rounded-t-lg md:rounded-l-lg md:rounded-t-none bg-gray-100"
              />
            </div>
          </div>

          <div className="w-full md:w-[350px] bg-white p-4 md:p-6 overflow-y-auto border-t md:border-t-0 md:border-l border-gray-100 relative max-h-[45vh] md:max-h-[95vh]">
            {/* Header section */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2 leading-tight">{item.title}</h2>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  <Heart className={`h-4 w-4 ${item.isLiked ? "fill-red-500 text-red-500" : ""}`} />
                  <span className="text-xs font-medium">{item.likeCount} likes</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  {(() => {
                    const IconComponent = getRoomTypeIcon(item.roomType)
                    return <IconComponent className="h-4 w-4 text-teal-600" />
                  })()}
                  Room Information
                </h3>
                <div className="space-y-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      {(() => {
                        const IconComponent = getRoomTypeIcon(item.roomType)
                        return <IconComponent className="h-4 w-4 text-teal-500" />
                      })()}
                      <div>
                        <p className="text-xs font-medium text-gray-500">Room Type</p>
                        <p className="text-sm font-semibold text-gray-900">{displayRoomType}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <Palette className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="text-xs font-medium text-gray-500">Room Theme</p>
                        <p className="text-sm font-semibold text-gray-900">{displayRoomTheme}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <Ruler className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="text-xs font-medium text-gray-500">Dimension</p>
                        <p className="text-sm font-semibold text-gray-900">{displayRoomDimension}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Palette className="h-4 w-4 text-teal-600" />
                  Design Preferences
                </h3>
                <div className="space-y-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-500 mb-2">Preferred Colors</p>
                    <div className="flex flex-wrap gap-1.5">
                      {preferredColors.map((color, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-1.5 bg-white rounded-full px-2.5 py-1 border border-gray-200"
                        >
                          <div
                            className="w-2.5 h-2.5 rounded-full border border-gray-300"
                            style={{ backgroundColor: getColorHex(color) }}
                          ></div>
                          <span className="text-xs font-medium text-gray-700">{color}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-500 mb-2">Avoided Colors</p>
                    {avoidedColors.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {avoidedColors.map((color, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-1.5 bg-red-50 rounded-full px-2.5 py-1 border border-red-200"
                          >
                            <div
                              className="w-2.5 h-2.5 rounded-full border border-red-300"
                              style={{ backgroundColor: getColorHex(color) }}
                            ></div>
                            <span className="text-xs font-medium text-red-700">{color}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-400 italic">None</div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-teal-600" />
                  Generation Settings
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-50 rounded-lg p-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Eye className="h-3 w-3 text-gray-400" />
                      <p className="text-xs font-medium text-gray-500">Mood</p>
                    </div>
                    <p className="text-xs font-semibold text-gray-900">{getMoodDisplayName(item.mood || "balanced")}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Zap className="h-3 w-3 text-gray-400" />
                      <p className="text-xs font-medium text-gray-500">Intensity</p>
                    </div>
                    <p className="text-xs font-semibold text-gray-900">
                      {getIntensityDisplayName(item.intensity || "medium")}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Wall className="h-3 w-3 text-gray-400" />
                      <p className="text-xs font-medium text-gray-500">Accent Wall</p>
                    </div>
                    <p className="text-xs font-semibold text-gray-900">{item.accentWall ? "Yes" : "No"}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Calendar className="h-3 w-3 text-gray-400" />
                      <p className="text-xs font-medium text-gray-500">Created</p>
                    </div>
                    <p className="text-xs font-semibold text-gray-900">{formatDate(item.createdAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
